import { z } from 'zod';
import { FieldKeySchema, FilterOpSchema } from '../fields/registry';

/**
 * Field references inside the AST are snake_case system names or `cf_*` custom keys.
 * JSON property names of the AST itself are camelCase.
 */
export const SYSTEM_FIELDS = [
  'id',
  'key',
  'number',
  'project_id',
  'issue_type_id',
  'status_id',
  'status.category',
  'priority_id',
  'priority.rank',
  'summary',
  'description',
  'reporter_id',
  'assignee_id',
  'created_by',
  'organization_id',
  'labels',
  'watchers',
  'parent_id',
  'created_at',
  'updated_at',
  'resolved_at',
  'due_at',
  'acknowledged_at',
  'acknowledged_by',
  /** Virtual field: full-text search over summary, description and comments. */
  'text',
] as const;
export const SystemFieldSchema = z.enum(SYSTEM_FIELDS);
export type SystemField = z.infer<typeof SystemFieldSchema>;

export const FieldRefSchema = z.union([SystemFieldSchema, FieldKeySchema]);
export type FieldRef = z.infer<typeof FieldRefSchema>;

/** Token resolved server-side to the caller's user id. */
export const ME_TOKEN = '$me';

export const ConditionSchema = z.strictObject({
  field: FieldRefSchema,
  op: FilterOpSchema,
  value: z.unknown().optional(),
});
export type Condition = z.infer<typeof ConditionSchema>;

export type FilterNode =
  | Condition
  | { and: FilterNode[] }
  | { or: FilterNode[] }
  | { not: FilterNode };

export const FilterNodeSchema: z.ZodType<FilterNode> = z.lazy(() =>
  z.union([
    ConditionSchema,
    z.strictObject({ and: z.array(FilterNodeSchema).min(1).max(50) }),
    z.strictObject({ or: z.array(FilterNodeSchema).min(1).max(50) }),
    z.strictObject({ not: FilterNodeSchema }),
  ]),
);

export const SortSchema = z.strictObject({
  field: FieldRefSchema,
  dir: z.enum(['asc', 'desc']).default('asc'),
});
export type Sort = z.infer<typeof SortSchema>;

export const GroupBySchema = z.strictObject({
  field: FieldRefSchema,
  limitPerGroup: z.int().min(1).max(100).default(20),
});

export const PAGE_SIZE_DEFAULT = 50;
export const PAGE_SIZE_MAX = 200;

export const PageSchema = z.strictObject({
  size: z.int().min(1).max(PAGE_SIZE_MAX).default(PAGE_SIZE_DEFAULT),
  cursor: z.string().max(4096).optional(),
});

export const SearchRequestSchema = z.strictObject({
  filter: FilterNodeSchema.optional(),
  sort: z.array(SortSchema).max(3).default([]),
  groupBy: GroupBySchema.optional(),
  select: z.array(FieldRefSchema).max(50).optional(),
  page: PageSchema.default({ size: PAGE_SIZE_DEFAULT }),
});
export type SearchRequest = z.infer<typeof SearchRequestSchema>;
export type SearchRequestInput = z.input<typeof SearchRequestSchema>;

/* ----------------------------- cursors ----------------------------- */

const toBase64Url = (bytes: Uint8Array): string => {
  let bin = '';
  for (const b of bytes) {
    bin += String.fromCharCode(b);
  }
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

const fromBase64Url = (s: string): Uint8Array => {
  const padded = s
    .replace(/-/g, '+')
    .replace(/_/g, '/')
    .padEnd(Math.ceil(s.length / 4) * 4, '=');
  const bin = atob(padded);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) {
    out[i] = bin.charCodeAt(i);
  }
  return out;
};

/** Keyset cursor: the last row's sort values followed by its id, as base64url JSON. */
export function encodeCursor(values: readonly unknown[]): string {
  return toBase64Url(new TextEncoder().encode(JSON.stringify(values)));
}

export function decodeCursor(cursor: string): unknown[] {
  let parsed: unknown;
  try {
    parsed = JSON.parse(new TextDecoder().decode(fromBase64Url(cursor)));
  } catch {
    throw new Error('malformed cursor');
  }
  if (!Array.isArray(parsed)) {
    throw new Error('malformed cursor');
  }
  return parsed;
}

/** Walks a filter tree and returns every referenced field (deduplicated). */
export function collectFilterFields(
  node: FilterNode | undefined,
  out = new Set<string>(),
): Set<string> {
  if (!node) {
    return out;
  }
  if ('and' in node) {
    for (const child of node.and) {
      collectFilterFields(child, out);
    }
  } else if ('or' in node) {
    for (const child of node.or) {
      collectFilterFields(child, out);
    }
  } else if ('not' in node) {
    collectFilterFields(node.not, out);
  } else {
    out.add(node.field);
  }
  return out;
}
